"use client";

import { useLazyQuery, useMutation } from "@apollo/client";
import {
  LOGO_LIGHT,
  REFRESH_TOKEN_PERSIST,
  USER_TOKEN_PERSIST
} from "@constants/global";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlUser } from "gql";
import { ISocialNetworkLogInRes } from "gql/User/mutations";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import router from "next/router";
import toast from "react-hot-toast";
import { PRIVATE_ROUTES } from "routes/routes";
import ToggleLang from "components/ToggleLang/ToggleLang";
import ToggleTheme from "components/ToggleTheme/ToggleTheme";
import { APP_VERSION } from "components/Roadmap/ItemPathNode/constants";
import {
  CardFooter,
  CardWelcome,
  AuthPageLayout,
  Button,
  Card,
  CardHeader
} from "components/ui/v2";
import LoginFormAurora from "./components/LoginFormAurora";
import { consumeFirstLoginToken } from "utils/safe-navigation";
import { useEffect, useState } from "react";

export default function LoginAurora() {
  const { query } = router;
  const { t } = useTranslation("common");
  const { invitationCode, redirect, loginsource } = query;
  const { vendor } = useVendor();

  const { setUser, setAccessToken, setRefreshToken } = useUserStoreContext(
    (store) => ({ ...store })
  );

  const [firstLogin, setFirstLogin] = useState<string | null>(null);

  useEffect(() => {
    setFirstLogin(consumeFirstLoginToken(loginsource as string | string[]));
  }, [loginsource]);

  const [getUser, { error: getUserError }] = useLazyQuery(
    gqlUser.queries.GET_FULL_USER,
    {
      fetchPolicy: "network-only",
      onCompleted({ user }) {
        setUser(user);
      },
      onError({ message }) {
        toast.error(message);
      }
    }
  );

  const onCompletedLogin = (login: ISocialNetworkLogInRes) => {
    const { accessToken, refreshToken } = login;
    localStorage.setItem(USER_TOKEN_PERSIST, `${accessToken}`);
    localStorage.setItem(REFRESH_TOKEN_PERSIST, `${refreshToken}`);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);

    if (firstLogin) {
      getUser({ variables: { accessToken } }).then(() => {
        router.push(PRIVATE_ROUTES.explore.path);
      });
    } else {
      getUser({ variables: { accessToken } });
    }
  };

  const [login, { loading: loginLoading }] = useMutation(
    gqlUser.queries.LOGIN_WITH_TOKEN,
    {
      onCompleted({ loginWithToken }) {
        onCompletedLogin(loginWithToken);
      },
      onError({ message }) {
        toast.error(message);
      }
    }
  );

  const handleLogin = () => {
    login({ variables: { token: firstLogin as string } });
  };

  const { user } = useUserStoreContext((store) => ({ user: store.user }));
  if (getUserError && !user) return null;

  const logo = vendor?.logo ? (
    <Image
      src={vendor.logo}
      width={400}
      height={300}
      priority
      sizes="(max-width: 768px) 100vw, 280px"
      alt={`${vendor?.name || "Vendor"} logo`}
      className="h-auto max-h-[160px] w-full max-w-[280px] object-contain"
    />
  ) : (
    <Image
      src={LOGO_LIGHT}
      width={400}
      height={300}
      alt="Groowcity"
      className="dark:brightness-0 dark:invert"
      priority
    />
  );

  return (
    <AuthPageLayout
      scenery="aurora"
      showAuroraEffects={false}
      controls={
        <>
          <ToggleLang />
          <ToggleTheme />
        </>
      }
    >
      <div className="flex w-full max-w-[430px] flex-col items-center gap-8 overflow-visible">
        <div className="w-full max-w-[280px]">{logo}</div>
        <Card
          variant="glow"
          entranceAnimation
          footer={<CardFooter>{APP_VERSION}</CardFooter>}
        >
          {!firstLogin ? (
            <>
              <CardHeader
                title={t("Bienvenido de vuelta")}
                description={t(
                  "Accede a tu espacio de trabajo y continúa donde lo dejaste."
                )}
              />

              <div className="mt-3 sm:mt-4">
                <LoginFormAurora
                  redirect={redirect as string}
                  invitationCode={invitationCode as string}
                />
              </div>
            </>
          ) : (
            <CardWelcome
              title={t("Bienvenido a Groowcity")}
              description={
                <>
                  {t("Hemos creado tu organización")}{" "}
                  <span className="font-semibold text-slate-700 dark:text-zinc-200">
                    {typeof window !== "undefined"
                      ? window.location.hostname
                      : ""}
                  </span>
                  .{" "}
                  {t(
                    "Haz clic en Ir a la Organización para disfrutar de la plataforma"
                  )}
                </>
              }
              action={
                <Button
                  width="full"
                  size="sm"
                  loading={loginLoading}
                  onClick={handleLogin}
                >
                  {t("Ir a la Organización")}
                </Button>
              }
            />
          )}
        </Card>
      </div>
    </AuthPageLayout>
  );
}
